// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { hasPtVersion } from "../../i18n/localizedPath";

const SITE_URL = "https://nihongotech.teqseq.com";
// The per-page <title> suffix (~20 routes use it) and og:site_name both use
// the exact brand name, "NihongoTech" - consistent with the JSON-LD @graph
// in public/index.html, which uses the same name/alternateName everywhere
// so Google resolves a brand query to one entity, not several variants.
const SITE_NAME = "NihongoTech";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

// react-helmet-async only replaces/removes tags it rendered itself (marked
// with its own data-rh attribute) - it has no way to know the static
// description/canonical/OG/Twitter tags in public/index.html are meant to
// be superseded, so it appends its tags alongside them instead of replacing
// them. Since every route now renders its own <Seo>, those static tags
// (marked data-default-seo - see index.html) are only a fallback for
// crawlers that never run this JS in the first place; once React has
// mounted here, they'd otherwise sit alongside Helmet's real ones as
// duplicate/conflicting meta tags. Strip them once, the first time any page
// actually mounts a <Seo>.
let staticFallbackTagsRemoved = false;
function removeStaticFallbackTags() {
    if (staticFallbackTagsRemoved) return;
    staticFallbackTagsRemoved = true;
    document.querySelectorAll("[data-default-seo]").forEach((tag) => tag.remove());
}

/**
 * Per-page title/description/canonical/OG tags. index.html only carries one
 * static set shared by every route - this is what makes each route report
 * its own instead, and matters more than it looks: an unchanging
 * <link rel="canonical"> pointing every subpage at "/" tells search engines
 * to index the homepage in that subpage's place.
 */
export function defaultAlternates(path: string): { hrefLang: string; path: string }[] {
    const isPtPath = path === "/pt" || path.startsWith("/pt/");
    const englishPath = isPtPath ? (path === "/pt" ? "/" : path.slice(3)) : path;
    if (!hasPtVersion(englishPath)) return [];
    const ptPath = englishPath === "/" ? "/pt" : `/pt${englishPath}`;
    return [
        { hrefLang: "en", path: englishPath },
        { hrefLang: "pt-PT", path: ptPath },
        { hrefLang: "x-default", path: englishPath },
    ];
}

function Seo({ title, description, path, image = DEFAULT_IMAGE, noindex = false, suffixBrand = true, lang, alternates, keywords }: { lang?: string; alternates?: { hrefLang: string; path: string }[]; keywords?: string[]; [key: string]: any }) {
    const { i18n } = useTranslation();
    const htmlLang = lang ?? (i18n.language === "pt" ? "pt-PT" : "en");
    const alternateLinks = noindex ? [] : alternates ?? defaultAlternates(path);
    const fullTitle = suffixBrand ? `${title} | ${SITE_NAME}` : title;
    const canonical = `${SITE_URL}${path}`;

    useEffect(() => {
        removeStaticFallbackTags();
    }, []);

    return (
        <Helmet htmlAttributes={{ lang: htmlLang }}>
            <title>{fullTitle}</title>
            {description && <meta name="description" content={description} />}
            {keywords?.length > 0 && <meta name="keywords" content={keywords.join(", ")} />}
            <link rel="canonical" href={canonical} />
            {noindex && <meta name="robots" content="noindex" />}
            {alternateLinks.map((alternate) => (
                <link key={alternate.hrefLang} rel="alternate" hrefLang={alternate.hrefLang} href={`${SITE_URL}${alternate.path}`} />
            ))}

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={fullTitle} />
            {description && <meta property="og:description" content={description} />}
            <meta property="og:image" content={image} />

            <meta name="twitter:card" content="summary" />
            <meta name="twitter:title" content={fullTitle} />
            {description && <meta name="twitter:description" content={description} />}
            <meta name="twitter:image" content={image} />
        </Helmet>
    );
}

export default Seo;
